import React, { useEffect, useLayoutEffect } from 'react';

// Style
import './PurchaseForm.scss';

// Redux connect
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// Classname
import clsx from 'clsx';

// Hooks
import { useWindowSize } from 'lib/hooks';

// components
import Divider from 'components/Divider';
import Button from 'components/UiKit/Button';
import TextField from 'components/UiKit/FieldText';
import ErrorBoundary from 'components/ErrorBoundary';
import FieldRadio from 'components/UiKit/FieldRadio';
import { Controller, useForm } from 'react-hook-form';

// Lib
import Cookies from 'js-cookie';
import { yupResolver } from '@hookform/resolvers/yup';
import { amplitudeInstance, events } from 'constans/cookie';
import { decodeHtmlEntity } from 'lib/helpers';

// Actions
import * as actionsCreator from 'pages/RequestForm/reducer';

// Navigation
import { useNavigate } from 'react-router-dom';

// Constants
import { excludeConfigPackaging, excludeWaterSolution, expandedWaterSolution } from 'constans/request-form';

// Schema
import { schema, FormData } from './PurchaseFormSchema';

interface IRequestFrom {
	title?: string,
	className?: string,
	submitText?: string,
	isHiddeImg?: boolean,
	textLabelButton?: string,
}

export const RequestForm: React.FC<IRequestFrom> = (props) => {
	const {
		title,
		className,
		isHiddeImg,
		submitText
	} = props;

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const auth = JSON.parse(localStorage.getItem('auth') || '{}');
	const localStoreFormat = JSON.parse(localStorage.getItem('format') || '{}');

	const {
		status,
		message,
		loading,
		radioBlock2,
		radioBlock3,
		optionsTitle,
		waterOptions,
		packagingGroup
	} = useAppSelector(
		(store) => store.requestForm,
		shallowEqual
	);

	const { width: screenWidth } = useWindowSize();
	const isMobile = screenWidth < 650;
	const isTablet = screenWidth < 1024;
	const dividerSize = isMobile ? 20 : 40;

	const other = 'Other';
	const solution = 'solution';
	const dryPowder = 'Dry powder';

	const name = localStoreFormat?.name;
	const param = localStoreFormat?.param;
	const format = localStoreFormat?.format;
	const catNumber = localStoreFormat?.catNumber;
	const compoundsCount = localStoreFormat?.compoundsCount || '';

	const paramWater = excludeWaterSolution.includes(format) ? format : '';
	const compoundFormat = expandedWaterSolution.includes(param)
		? format === dryPowder
			? dryPowder : solution : excludeConfigPackaging.includes(param)
			? solution : format === dryPowder
				? dryPowder : '';
	const packagingGroupRadio = excludeConfigPackaging.includes(param)
		? param : param === other
			? other : '';
	const inputDryPowder = format === dryPowder ? param === other ? '' : param : '';
	const inquiry = `${name ? decodeHtmlEntity(name) : ''} ${catNumber ? catNumber : ''}`?.replace(/<\/?[^>]+(>|$)/g, '');

	const defaultValues = {
		title: auth?.title || '',
		email: auth?.email || '',
		lastName: auth?.lastName || '',
		firstName: auth?.firstName || '',
		companyName: auth?.organization || '',

		target: inquiry,
		inquiry: inquiry,
		dmsoWater: paramWater,
		format: inputDryPowder,
		compoundFormat: compoundFormat,
		packagingGroup: packagingGroupRadio,
		setSize: compoundsCount ? `Library contains ${compoundsCount} compounds. Cherry-picking is possible.` : ''
	};

	const {
		watch,
		reset,
		control,
		register,
		getValues,
		handleSubmit,
		formState: { errors }
	} = useForm<FormData>({
		resolver: yupResolver(schema),
		defaultValues
	});

	const watcherData = watch();

	useEffect(() => {
		const isCookie = Cookies.get('cookieName');

		if (isCookie) {
			amplitudeInstance.logEvent(events.request_form);
		}
	}, []);

	// Scroll to top after navigating
	useLayoutEffect(() => {
		window.scrollTo(0, 0);
	}, [navigate]);

	useEffect(() => {
		if (watcherData.compoundFormat === 'Dry powder') {
			reset({
				...getValues(),
				'solution': '',
				'dmsoWater': '',
				'packagingGroup': ''
			});
		}

		if (watcherData.compoundFormat === 'solution') {
			reset({
				...getValues(),
				'format': ''
			});
		}
	}, [watcherData.compoundFormat]);

	const onSubmit = (data: FormData) => {
		dispatch(actionsCreator.sendForm({ data, navigate }));

		const isCookie = Cookies.get('cookieName');

		if (isCookie) {
			amplitudeInstance.logEvent(events.request_form_send);
		}
	};

	const compoundFormatOptions = [
		{
			value: 'Dry powder',
			label: 'Dry powder',
			children: <TextField
				required
				name="format"
				placeholder="Your answer"
				isError={!!errors?.format}
				disabled={watcherData.compoundFormat !== dryPowder}
				msgError={errors?.format ? errors?.format.message : ''}
				register={register('format', { required: true })}
				className="mb40 pl30 full-width purchase-form-screen__wrapper-component"
			/>
		},
		{
			value: 'solution',
			label: 'Solution *',
			children: <TextField
				required
				name="solution"
				placeholder="Your answer"
				isError={!!errors?.solution}
				disabled={watcherData.packagingGroup !== other}
				msgError={errors?.solution ? errors?.solution.message : ''}
				register={register('solution', { required: true })}
				className="purchase-form-screen__wrapper-component full-width pl30"
				children={
					<div className="flex flex-ai-start">
						<Controller
							control={control}
							name="packagingGroup"
							rules={{ required: true }}
							render={({ field: { onChange, value } }) => {
								return (
									<FieldRadio
										isColumn
										onChange={onChange}
										name="packagingGroup"
										options={packagingGroup}
										classNameBorderWrapper="mb10"
										isError={!!errors?.packagingGroup}
										disabled={watcherData.compoundFormat !== solution}
										classNameWrapper="radio-group-title-wrapper flex-jc-between"
										register={register('packagingGroup', { required: false })}
										msgError={errors?.packagingGroup ? errors?.packagingGroup?.message : ''}
									/>
								);
							}}
						/>
						<Controller
								name="dmsoWater"
								control={control}
								rules={{ required: true }}
								render={({ field: { onChange, value } }) => {
									return (
										<FieldRadio
											isColumn
											name="dmsoWater"
											onChange={onChange}
											options={waterOptions}
											isError={!!errors?.dmsoWater}
											classNameBorderWrapper="mb10"
											disabled={watcherData.compoundFormat !== solution}
											register={register('dmsoWater', { required: false })}
											msgError={errors?.dmsoWater ? errors?.dmsoWater?.message : ''}
											classNameWrapper="radio-group-title-wrapper purchase-form-screen__wrapper-solution flex-jc-between"
										/>
									);
								}}
							/>
					</div>
				}
			/>
		}
	];

	const cn = clsx(
		'purchase-form-screen',
		isHiddeImg ? 'purchase-form-screen__hidden-img' : '',
		className ? className : ''
	);

	return (
		<ErrorBoundary>
			<div className={cn}>
				<div className="wrapper relative">
					<h1
						className="purchase-form-screen__title"
						dangerouslySetInnerHTML={{
							__html: title || 'Bioactive compounds purchase form'
						}}
					/>

					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="purchase-form-screen__form">
							<div className="purchase-form-screen__wrapper-title-radio mb40">
								<h4 className="purchase-form-screen__title-radio">
									Title
								</h4>

								<Controller
									name="title"
									control={control}
									rules={{ required: true }}
									render={({ field: { onChange, value } }) => {
										return (
											<FieldRadio
												name="title"
												isColumn={isMobile}
												onChange={onChange}
												options={optionsTitle}
												isError={!!errors?.title}
												register={register('title', { required: true })}
												msgError={errors?.title ? errors?.title?.message : ''}
												className="purchase-form-screen__wrapper-title-radio-p"
												classNameWrapper="radio-group-title-wrapper flex-jc-between"
												classNameLabel="purchase-form-screen__wrapper-title-radio-label"
												classNameBorderWrapper="purchase-form-screen__wrapper-title-radio-block"
											/>
										);
									}}
								/>
							</div>

							<div className="flex flex-ai-center flex-jc-between flex-direction-column-desktop">
								<TextField
									required
									name="firstName"
									label="First name *"
									placeholder="Your answer"
									isError={!!errors?.firstName}
									className="purchase-form-screen__form-col"
									register={register('firstName', { required: true })}
								/>

								{isTablet && <Divider size={dividerSize} />}

								<TextField
									required
									name="lastName"
									label="Last name *"
									placeholder="Your answer"
									isError={!!errors?.lastName}
									className="purchase-form-screen__form-col"
									msgError={errors?.lastName ? errors?.lastName.message : ''}
									register={register('lastName', { required: true })}
								/>
							</div>

							<Divider size={dividerSize} />

							<div className="flex flex-ai-center flex-jc-between flex-direction-column-desktop">
								<TextField
									required
									name="companyName"
									label="Company name *"
									placeholder="Your answer"
									isError={!!errors?.companyName}
									className="purchase-form-screen__form-col"
									register={register('companyName', { required: true })}
								/>

								{isTablet && <Divider size={dividerSize} />}

								<TextField
									required
									name="email"
									label="Email *"
									isError={!!errors?.email}
									placeholder="Your answer"
									className="purchase-form-screen__form-col"
									msgError={errors?.email ? errors?.email.message : ''}
									register={register('email', { required: true, pattern: /^\S+@\S+$/i })}
								/>
							</div>

							<Divider size={dividerSize} />

							<TextField
								required
								name="inquiry"
								placeholder="Your answer"
								label="Inquiry description *"
								register={register('inquiry', { required: true })}
								subLabel="Give as many biological details of your study as possible, this would help our team to select better quality compounds set for your needs"
							/>

							<Divider size={dividerSize} />

							<TextField
								required
								name="target"
								placeholder="Your answer"
								label="Targets/Target families *"
								register={register('target', { required: true })}
								subLabel="Please type N/A if target description is irrelevant to your study"
							/>

							<Divider size={dividerSize} />

							<p className="purchase-form-screen__title-format">
								Required compound format *
							</p>
							<Controller
								control={control}
								name="compoundFormat"
								rules={{ required: true }}
								render={({ field: { onChange, value } }) => {
									return (
										<FieldRadio
											isColumn
											className="mb10"
											onChange={onChange}
											name="compoundFormat"
											options={compoundFormatOptions}
											isError={!!errors?.compoundFormat}
											register={register('compoundFormat', { required: true })}
											msgError={errors?.compoundFormat ? errors?.compoundFormat?.message : ''}
										/>
									);
								}}
							/>

							<Divider size={dividerSize} />

							<TextField
								required
								name="compoundsPurity"
								placeholder="Your answer"
								isError={!!errors?.compoundsPurity}
								label="Required compound purity, % *"
								disabled={watcherData.groupRadio2 !== 'Other3'}
								msgError={errors?.compoundsPurity ? errors?.compoundsPurity?.message : ''}
								register={register('compoundsPurity', { required: 'Field is required' })}
								subLabel="Selecting a custom purity option in many cases will lead to increased shipment time and will affect pricing."
								children={
									<div className="purchase-form-screen__checkbox">
										<Controller
											control={control}
											name="groupRadio2"
											rules={{ required: true }}
											render={({ field: { onChange, value } }) => {
												return (
													<FieldRadio
														isColumn
														className="mb10"
														name="groupRadio2"
														onChange={onChange}
														options={radioBlock2}
														isError={!!errors?.groupRadio2}
														msgError={errors?.groupRadio2 ? errors?.groupRadio2.message : ''}
														register={register('groupRadio2', { required: 'Field is required' })}
													/>
												);
											}}
										/>
									</div>
								}
							/>

							<Divider size={dividerSize} />

							<TextField
								required
								name="activityRange"
								placeholder="Your answer"
								isError={!!errors?.activityRange}
								label="Preferred activity range, uM *"
								disabled={watcherData.groupRadio3 !== 'Other4'}
								msgError={errors?.activityRange ? errors?.activityRange.message : ''}
								register={register('activityRange', { required: 'Preferred activity range is required' })}
								children={
									<div className="purchase-form-screen__checkbox">
										<Controller
											control={control}
											name="groupRadio3"
											rules={{ required: true }}
											render={({ field: { onChange, value } }) => {
												return (
													<FieldRadio
														isColumn
														className="mb10"
														name="groupRadio3"
														onChange={onChange}
														options={radioBlock3}
														isError={!!errors?.groupRadio3}
														msgError={errors?.groupRadio3 ? errors?.groupRadio3.message : ''}
														register={register('groupRadio3', { required: true })}
													/>
												);
											}}
										/>
									</div>
								}
								subLabel="Please type N/A if compound activity is an unimportant factor for your study, for instance, you opt for clinically approved compounds."
							/>

							<Divider size={dividerSize} />

							<TextField
								name="selectivity"
								placeholder="Your answer"
								label="Preferred selectivity:"
								subLabel="Please describe selectivity if applicable."
								register={register('selectivity', { required: false })}
							/>

							<Divider size={dividerSize} />

							<TextField
								name="setSize"
								placeholder="Your answer"
								label="Approximate expected set size"
								register={register('setSize', { required: false })}
								subLabel="Please type the number of compounds grounding on your research plan and budget."
							/>
						</div>

						{status === 200 && !!message && <p className="purchase-form-screen__success-text">{message}</p>}
						{status !== 200 && !!message && <p className="purchase-form-screen__success-error">{message}</p>}

						<Button
							type="submit"
							variable="info"
							loading={loading}
							className="centered-button"
							label={submitText || 'Send'}
						/>
					</form>
				</div>
			</div>
		</ErrorBoundary>
	);
};

export default RequestForm;